import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from '../../../hooks';
import proofkitFilemakerService from '../../../services/proofkitFilemakerService';
import { ArrowLeft, Edit, Send, Trash } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Label, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui';

const CAMPAIGNS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CAMPAIGNS || "campaigns";
const CAMPAIGN_RECIPIENTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CAMPAIGN_RECIPIENTS || "campaign_recipients";
const CONTACTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CONTACTS || "contacts";

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        const res = await proofkitFilemakerService.getRecord(CAMPAIGNS_LAYOUT, id);
        const camp = res?.data ? res.data : null;
        setCampaign(camp);

        if (camp) {
          const campKey = camp.fieldData?.ClavePrincipal || id;
          const recRes = await proofkitFilemakerService.findRecords(CAMPAIGN_RECIPIENTS_LAYOUT, {
            query: [{ campaign_id: campKey }],
            limit: 500,
          });
          const recs = Array.isArray(recRes?.data) ? recRes.data : [];
          setRecipients(recs);

          if (recs.length > 0) {
            // Cargar info de contactos
            const contactIds = [...new Set(recs.map((r) => r.fieldData?.contact_id).filter(Boolean))];
            if (contactIds.length > 0) {
              const contactsData = [];
              // Fetch contacts one by one or optimized find (FileMaker Data API doesn't support IN query easily without multiple requests or find requests)
              // We'll use multiple find requests in one call if possible, but findRecords takes one query object or array of objects (OR search).
              // Let's try OR search
              const query = contactIds.map((cid) => ({ ClavePrincipal: cid }));
              const cRes = await proofkitFilemakerService.findRecords(CONTACTS_LAYOUT, { query, limit: 500 });
              if (cRes?.data) contactsData.push(...cRes.data);
              setContacts(contactsData);
            }
          }
        }
      } catch (err) {
        toast({
          title: "Error",
          description: "No se pudo cargar la campaña",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, toast]);

  const campaignData = useMemo(() => {
    if (!campaign?.fieldData) return null;
    const fd = campaign.fieldData;
    return {
      id: fd.ClavePrincipal || id,
      nombre: fd.nombre || "Sin nombre",
      estado: fd.estado || "borrador",
      template_id: fd.template_id || "",
      fecha_envio: fd.fecha_envio || "",
      total_recipients: Number(fd.total_recipients || 0),
      total_enviados: Number(fd.total_enviados || 0),
      total_aperturas: Number(fd.total_aperturas || 0),
      total_bounced: Number(fd.total_bounced || 0),
      tasa_apertura: Number(fd.tasa_apertura || 0),
    };
  }, [campaign, id]);

  const recipientsData = useMemo(() => {
    return recipients.map(r => {
      const fd = r?.fieldData || {};
      const contact = contacts.find(c => c?.fieldData?.ClavePrincipal === fd.contact_id);
      const contactData = contact?.fieldData || {};
      
      return {
        id: fd.ClavePrincipal || r?.recordId || "",
        contact_id: fd.contact_id || "",
        estado: fd.estado || "pendiente",
        fecha_envio: fd.fecha_envio || "",
        fecha_apertura: fd.fecha_apertura || "",
        bounced: Boolean(fd.bounced),
        contact_name: `${contactData.nombre || ""} ${contactData.apellido || ""}`.trim(),
        contact_email: contactData.email || "",
      };
    });
  }, [recipients, contacts]);

  const getEstadoBadge = (estado) => {
    const v = String(estado || "").toLowerCase();
    if (!v) return <Badge variant="secondary">Sin estado</Badge>;
    if (v === "enviado" || v === "sent") return <Badge variant="default">Enviado</Badge>;
    if (v === "pendiente") return <Badge variant="outline">Pendiente</Badge>;
    if (v === "fallido" || v === "failed") return <Badge variant="destructive">Fallido</Badge>;
    if (v === "rebotado" || v === "bounced") return <Badge variant="destructive">Rebotado</Badge>;
    return <Badge variant="secondary">{estado}</Badge>;
  };

  const getCampaignEstadoBadge = (estado) => {
    const v = String(estado || "").toLowerCase();
    if (!v) return <Badge variant="secondary">Sin estado</Badge>;
    if (v === "enviada" || v === "sent") return <Badge variant="default">Enviada</Badge>;
    if (v === "programada" || v === "scheduled") return <Badge variant="outline">Programada</Badge>;
    if (v === "borrador" || v === "draft") return <Badge variant="secondary">Borrador</Badge>;
    if (v === "cancelada" || v === "cancelled") return <Badge variant="destructive">Cancelada</Badge>;
    return <Badge variant="secondary">{estado}</Badge>;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/campanas")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
        </div>
        <div className="text-center py-8">Cargando campaña...</div>
      </div>
    );
  }

  if (!campaignData) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/campanas")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
        </div>
        <div className="text-center py-8 text-[var(--yool-error)]">Campaña no encontrada</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate("/campanas")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/campanas/${id}/edit`)}>
            <Edit className="h-4 w-4 mr-2" />
            Editar
          </Button>
          {campaignData.estado === "borrador" && (
            <Button>
              <Send className="h-4 w-4 mr-2" />
              Enviar
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Estado</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{getCampaignEstadoBadge(campaignData.estado)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Destinatarios</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{campaignData.total_recipients}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Enviados</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{campaignData.total_enviados}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tasa Apertura</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{campaignData.tasa_apertura.toFixed(1)}%</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Detalles de la Campaña</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground">Nombre</Label>
              <p className="font-medium">{campaignData.nombre}</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">ID Plantilla</Label>
              <p className="font-medium">{campaignData.template_id}</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Fecha de Envío</Label>
              <p className="font-medium">{campaignData.fecha_envio || "No programada"}</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Aperturas</Label>
              <p className="font-medium">{campaignData.total_aperturas}</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Rebotes</Label>
              <p className="font-medium">{campaignData.total_bounced}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Destinatarios ({recipientsData.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contacto</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Fecha Envío</TableHead>
                  <TableHead>Fecha Apertura</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recipientsData.length ? (
                  recipientsData.map((recipient) => (
                    <TableRow key={recipient.id}>
                      <TableCell className="font-medium">{recipient.contact_name || "Sin nombre"}</TableCell>
                      <TableCell>{recipient.contact_email || "-"}</TableCell>
                      <TableCell>{getEstadoBadge(recipient.estado)}</TableCell>
                      <TableCell>{recipient.fecha_envio || "-"}</TableCell>
                      <TableCell>{recipient.fecha_apertura || "-"}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center">
                      No hay destinatarios.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
